import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { locationAPI, uploadAPI } from '../services/api';
import { toast } from 'react-toastify';
import { FiUpload, FiX, FiSave, FiMapPin, FiMic, FiSquare, FiTrash2 } from 'react-icons/fi';
import MapPicker from '../components/MapPicker';
import './LocationForm.css';

const LocationForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEdit = Boolean(id);

  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);

  // Voice recording states
  const [isRecording, setIsRecording] = useState(false);
  const [audioBlob, setAudioBlob] = useState(null);
  const [audioUrl, setAudioUrl] = useState(null);
  const [recordingTime, setRecordingTime] = useState(0);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const timerRef = useRef(null);

  const [formData, setFormData] = useState({
    label: '',
    type: 'home',
    coordinates: { latitude: '', longitude: '' },
    address: {
      full: '',
      flatNumber: '',
      floor: '',
      building: '',
      tower: '',
      landmark: '',
      pincode: ''
    },
    deliveryInstructions: {
      directions: '',
      entryGate: '',
      securityMessage: '',
      parkingInfo: '',
      specialNotes: '',
      voiceInstruction: null // { url, duration }
    },
    gateSystem: {
      type: 'none',
      societyCode: '',
      flatCode: ''
    },
    photos: [],
    isDefault: false
  });

  useEffect(() => {
    if (isEdit) {
      fetchLocation();
    }
    // Get current location
    if (navigator.geolocation && !isEdit) {
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          setFormData(prev => ({
            ...prev,
            coordinates: {
              latitude: pos.coords.latitude,
              longitude: pos.coords.longitude
            }
          }));
        },
        (err) => console.log('Geolocation error:', err)
      );
    }
  }, [id, isEdit]);

  const fetchLocation = async () => {
    try {
      const response = await locationAPI.getById(id);
      const loc = response.data.location;
      setFormData({
        label: loc.label || '',
        type: loc.type || 'home',
        coordinates: loc.coordinates || { latitude: '', longitude: '' },
        address: loc.address || {},
        deliveryInstructions: loc.deliveryInstructions || {},
        gateSystem: loc.gateSystem || { type: 'none' },
        photos: loc.photos || [],
        isDefault: loc.isDefault || false
      });
    } catch (error) {
      toast.error('Failed to fetch location');
      navigate('/locations');
    }
  };

  const handleChange = (e, section = null) => {
    const { name, value, type, checked } = e.target;
    const val = type === 'checkbox' ? checked : value;

    if (section) {
      setFormData(prev => ({
        ...prev,
        [section]: { ...prev[section], [name]: val }
      }));
    } else {
      setFormData(prev => ({ ...prev, [name]: val }));
    }
  };

  const handlePhotoUpload = async (e) => {
    const files = e.target.files;
    if (!files.length) return;

    setUploading(true);
    try {
      for (const file of files) {
        const response = await uploadAPI.uploadPhoto(file, '', 'landmark');
        setFormData(prev => ({
          ...prev,
          photos: [...prev.photos, response.data.photo]
        }));
      }
      toast.success('Photos uploaded');
    } catch (error) {
      toast.error('Failed to upload photos');
    } finally {
      setUploading(false);
    }
  };

  const handleRemovePhoto = (index) => {
    setFormData(prev => ({
      ...prev,
      photos: prev.photos.filter((_, i) => i !== index)
    }));
  };

  // Voice recording functions
  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      mediaRecorderRef.current = new MediaRecorder(stream);
      audioChunksRef.current = [];

      mediaRecorderRef.current.ondataavailable = (event) => {
        audioChunksRef.current.push(event.data);
      };

      mediaRecorderRef.current.onstop = () => {
        const blob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
        setAudioBlob(blob);
        setAudioUrl(URL.createObjectURL(blob));
        stream.getTracks().forEach(track => track.stop());
      };

      mediaRecorderRef.current.start();
      setIsRecording(true);
      setRecordingTime(0);

      // Timer for recording duration
      timerRef.current = setInterval(() => {
        setRecordingTime(prev => prev + 1);
      }, 1000);

    } catch (error) {
      console.error('Microphone access error:', error);
      toast.error('Could not access microphone. Please allow microphone permission.');
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
      clearInterval(timerRef.current);
    }
  };

  const deleteRecording = () => {
    setAudioBlob(null);
    setAudioUrl(null);
    setRecordingTime(0);
    setFormData(prev => ({
      ...prev,
      deliveryInstructions: {
        ...prev.deliveryInstructions,
        voiceInstruction: null
      }
    }));
  };

  const uploadVoiceInstruction = async () => {
    if (!audioBlob) return null;

    try {
      const formDataUpload = new FormData();
      formDataUpload.append('audio', audioBlob, 'voice-instruction.webm');

      const response = await uploadAPI.uploadAudio(formDataUpload);
      return {
        url: response.data.url,
        duration: recordingTime
      };
    } catch (error) {
      console.error('Voice upload error:', error);
      toast.error('Failed to upload voice instruction');
      return null;
    }
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.label || !formData.address.full) {
      toast.error('Please fill in required fields');
      return;
    }

    if (!formData.coordinates.latitude || !formData.coordinates.longitude) {
      toast.error('Please provide location coordinates');
      return;
    }

    setLoading(true);
    try {
      let submitData = { ...formData };

      // Upload voice instruction if recorded
      if (audioBlob) {
        const voiceData = await uploadVoiceInstruction();
        if (voiceData) {
          submitData.deliveryInstructions = {
            ...submitData.deliveryInstructions,
            voiceInstruction: voiceData
          };
        }
      }

      if (isEdit) {
        await locationAPI.update(id, submitData);
        toast.success('Location updated');
      } else {
        await locationAPI.create(submitData);
        toast.success('Location created');
      }
      navigate('/locations');
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to save location');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="location-form-page">
      <div className="container">
        <h1>{isEdit ? 'Edit Location' : 'Add New Location'}</h1>

        <form onSubmit={handleSubmit} className="location-form">
          {/* Basic Info */}
          <div className="form-section">
            <h2>Basic Information</h2>
            <div className="form-row">
              <div className="form-group">
                <label className="form-label">Label *</label>
                <input
                  type="text"
                  name="label"
                  className="form-input"
                  placeholder="e.g., Home, Office, Parents"
                  value={formData.label}
                  onChange={handleChange}
                  required
                />
              </div>
              <div className="form-group">
                <label className="form-label">Type</label>
                <select
                  name="type"
                  className="form-input"
                  value={formData.type}
                  onChange={handleChange}
                >
                  <option value="home">Home</option>
                  <option value="work">Work</option>
                  <option value="other">Other</option>
                </select>
              </div>
            </div>
          </div>

          {/* Map Picker */}
          <div className="form-section">
            <h2><FiMapPin /> Select Location on Map</h2>
            <p className="section-desc">Search or click on the map to set your exact delivery location</p>
            <MapPicker
              coordinates={formData.coordinates}
              onLocationSelect={(coords) => {
                setFormData(prev => ({
                  ...prev,
                  coordinates: {
                    latitude: coords.latitude,
                    longitude: coords.longitude
                  }
                }));
              }}
              onAddressSelect={(address) => {
                setFormData(prev => ({
                  ...prev,
                  address: {
                    ...prev.address,
                    full: address.full || prev.address.full,
                    landmark: address.landmark || prev.address.landmark,
                    pincode: address.pincode || prev.address.pincode
                  }
                }));
              }}
              height="350px"
            />
          </div>

          {/* Address */}
          <div className="form-section">
            <h2>Address Details</h2>
            <div className="form-group">
              <label className="form-label">Full Address *</label>
              <input
                type="text"
                name="full"
                className="form-input"
                placeholder="Complete address"
                value={formData.address.full}
                onChange={(e) => handleChange(e, 'address')}
                required
              />
            </div>
            <div className="form-row">
              <div className="form-group">
                <label className="form-label">Building Name</label>
                <input
                  type="text"
                  name="building"
                  className="form-input"
                  placeholder="Building/Society name"
                  value={formData.address.building}
                  onChange={(e) => handleChange(e, 'address')}
                />
              </div>
              <div className="form-group">
                <label className="form-label">Tower/Block</label>
                <input
                  type="text"
                  name="tower"
                  className="form-input"
                  placeholder="Tower A, Block B"
                  value={formData.address.tower}
                  onChange={(e) => handleChange(e, 'address')}
                />
              </div>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label className="form-label">Flat/Door Number</label>
                <input
                  type="text"
                  name="flatNumber"
                  className="form-input"
                  placeholder="A-401"
                  value={formData.address.flatNumber}
                  onChange={(e) => handleChange(e, 'address')}
                />
              </div>
              <div className="form-group">
                <label className="form-label">Floor</label>
                <input
                  type="text"
                  name="floor"
                  className="form-input"
                  placeholder="4th Floor"
                  value={formData.address.floor}
                  onChange={(e) => handleChange(e, 'address')}
                />
              </div>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label className="form-label">Landmark</label>
                <input
                  type="text"
                  name="landmark"
                  className="form-input"
                  placeholder="Near City Mall"
                  value={formData.address.landmark}
                  onChange={(e) => handleChange(e, 'address')}
                />
              </div>
              <div className="form-group">
                <label className="form-label">Pincode</label>
                <input
                  type="text"
                  name="pincode"
                  className="form-input"
                  placeholder="560001"
                  value={formData.address.pincode}
                  onChange={(e) => handleChange(e, 'address')}
                />
              </div>
            </div>
            {/* Hidden coordinates - managed by map picker */}
            {formData.coordinates.latitude && formData.coordinates.longitude && (
              <div className="coordinates-info">
                <small>
                  📍 Location set: {parseFloat(formData.coordinates.latitude).toFixed(6)}, {parseFloat(formData.coordinates.longitude).toFixed(6)}
                </small>
              </div>
            )}
          </div>

          {/* Delivery Instructions */}
          <div className="form-section">
            <h2>Delivery Instructions</h2>
            <p className="section-desc">Help riders find your location easily</p>
            <div className="form-group">
              <label className="form-label">Step-by-step Directions</label>
              <textarea
                name="directions"
                className="form-input"
                rows="3"
                placeholder="After entering main gate, take first left. My building is the second one on the right..."
                value={formData.deliveryInstructions.directions}
                onChange={(e) => handleChange(e, 'deliveryInstructions')}
              />
            </div>
            <div className="form-row">
              <div className="form-group">
                <label className="form-label">Entry Gate</label>
                <input
                  type="text"
                  name="entryGate"
                  className="form-input"
                  placeholder="Gate 2 (Side entrance)"
                  value={formData.deliveryInstructions.entryGate}
                  onChange={(e) => handleChange(e, 'deliveryInstructions')}
                />
              </div>
              <div className="form-group">
                <label className="form-label">What to Tell Security</label>
                <input
                  type="text"
                  name="securityMessage"
                  className="form-input"
                  placeholder="Flat A-401, Tower A"
                  value={formData.deliveryInstructions.securityMessage}
                  onChange={(e) => handleChange(e, 'deliveryInstructions')}
                />
              </div>
            </div>
            <div className="form-group">
              <label className="form-label">Parking Info</label>
              <input
                type="text"
                name="parkingInfo"
                className="form-input"
                placeholder="Park near visitor parking"
                value={formData.deliveryInstructions.parkingInfo}
                onChange={(e) => handleChange(e, 'deliveryInstructions')}
              />
            </div>

            {/* Voice Instruction */}
            <div className="form-group voice-instruction-section">
              <label className="form-label">
                <FiMic /> Voice Instruction (Optional)
              </label>
              <p className="field-desc">Record voice directions for the delivery rider</p>

              {!audioUrl && !formData.deliveryInstructions.voiceInstruction ? (
                <div className="voice-recorder">
                  {isRecording ? (
                    <div className="recording-active">
                      <div className="recording-indicator">
                        <span className="pulse"></span>
                        <span>Recording... {formatTime(recordingTime)}</span>
                      </div>
                      <button
                        type="button"
                        className="btn btn-danger stop-btn"
                        onClick={stopRecording}
                      >
                        <FiSquare /> Stop
                      </button>
                    </div>
                  ) : (
                    <button
                      type="button"
                      className="btn btn-outline record-btn"
                      onClick={startRecording}
                    >
                      <FiMic /> Record Voice Instruction
                    </button>
                  )}
                </div>
              ) : (
                <div className="voice-playback">
                  <audio
                    controls
                    src={audioUrl || formData.deliveryInstructions.voiceInstruction?.url}
                    className="audio-player"
                  />
                  <button
                    type="button"
                    className="btn btn-outline btn-sm delete-audio"
                    onClick={deleteRecording}
                  >
                    <FiTrash2 /> Remove
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* Gate System */}
          <div className="form-section">
            <h2>Gate System</h2>
            <div className="form-row">
              <div className="form-group">
                <label className="form-label">Gate System Type</label>
                <select
                  name="type"
                  className="form-input"
                  value={formData.gateSystem.type}
                  onChange={(e) => handleChange(e, 'gateSystem')}
                >
                  <option value="none">None / Open Entry</option>
                  <option value="mygate">MyGate</option>
                  <option value="jiogate">JioGate</option>
                  <option value="nobroker">NoBroker</option>
                </select>
              </div>
            </div>
            {formData.gateSystem.type !== 'none' && (
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Society Code</label>
                  <input
                    type="text"
                    name="societyCode"
                    className="form-input"
                    placeholder="Society/Community code"
                    value={formData.gateSystem.societyCode}
                    onChange={(e) => handleChange(e, 'gateSystem')}
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Flat Code</label>
                  <input
                    type="text"
                    name="flatCode"
                    className="form-input"
                    placeholder="Your flat code"
                    value={formData.gateSystem.flatCode}
                    onChange={(e) => handleChange(e, 'gateSystem')}
                  />
                </div>
              </div>
            )}
          </div>

          {/* Photos */}
          <div className="form-section">
            <h2>Location Photos</h2>
            <p className="section-desc">Add photos to help riders identify your location</p>

            <div className="photos-grid">
              {formData.photos.map((photo, index) => (
                <div key={index} className="photo-item">
                  <img src={photo.url} alt={photo.caption || 'Location'} />
                  <button
                    type="button"
                    className="remove-photo"
                    onClick={() => handleRemovePhoto(index)}
                  >
                    <FiX />
                  </button>
                </div>
              ))}
              <label className="photo-upload">
                <input
                  type="file"
                  accept="image/*"
                  multiple
                  onChange={handlePhotoUpload}
                  disabled={uploading}
                />
                <FiUpload />
                <span>{uploading ? 'Uploading...' : 'Add Photos'}</span>
              </label>
            </div>
          </div>

          {/* Default */}
          <div className="form-section">
            <label className="checkbox-label">
              <input
                type="checkbox"
                name="isDefault"
                checked={formData.isDefault}
                onChange={handleChange}
              />
              <span>Set as default delivery location</span>
            </label>
          </div>

          {/* Submit */}
          <div className="form-actions">
            <button type="button" className="btn btn-outline" onClick={() => navigate('/locations')}>
              Cancel
            </button>
            <button type="submit" className="btn btn-primary" disabled={loading}>
              <FiSave />
              {loading ? 'Saving...' : (isEdit ? 'Update Location' : 'Save Location')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default LocationForm;
